import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CreateProjectModal } from "@/components/dashboard/CreateProjectModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, BookOpen, Loader2, ArrowRight, Gem, Archive, RotateCcw } from "lucide-react";

const MODE_LABELS = {
  single_narrator: "Single Narrator",
  hybrid: "Hybrid",
  full_cast: "Full Cast",
};

const STATUS_STYLES = {
  draft: "bg-slate-700/30 text-slate-300 ring-slate-500/30",
  in_production: "bg-amber-500/15 text-amber-300 ring-amber-500/30",
  review: "bg-sky-500/15 text-sky-300 ring-sky-500/30",
  approved: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
};

export function ProjectsDashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archived, setArchived] = useState([]);

  const load = () => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
    api.get("/projects", { params: { archived: true } }).then((r) => setArchived(r.data)).catch(() => setArchived([]));
  };

  useEffect(() => {
    load();
  }, []);

  const archiveProject = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Archive this project? It's hidden from your active list but fully recoverable — no data is deleted.")) return;
    try { await api.post(`/projects/${id}/archive`); toast.success("Project archived. Find it under Archived."); load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const restoreProject = async (id, e) => {
    e.stopPropagation();
    try { await api.post(`/projects/${id}/restore`); toast.success("Project restored."); load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const filtered = (projects || []).filter(
    (p) =>
      p.title.toLowerCase().includes(query.toLowerCase()) ||
      (p.author || "").toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500/70">
            Phase 1 · Foundation
          </div>
          <h1 className="font-serif text-4xl tracking-tight text-slate-100">Productions</h1>
          <p className="mt-2 text-sm text-slate-400">
            Private books under production. Each carries the locked Scott narrator preset.
          </p>
        </div>
        <Button
          data-testid="create-project-button"
          onClick={() => setOpen(true)}
          className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400"
        >
          <Plus className="h-4 w-4" /> New production
        </Button>
      </div>

      <div className="relative mb-6 max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
        <Input
          data-testid="project-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search productions…"
          className="bg-slate-950/60 pl-9"
        />
      </div>

      {projects === null ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-[#12161f]/40 py-24 text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/10 ring-1 ring-amber-500/25">
            <BookOpen className="h-6 w-6 text-amber-400" />
          </div>
          <h2 className="font-serif text-2xl text-slate-200">No productions yet</h2>
          <p className="mt-2 max-w-sm text-sm text-slate-500">
            Create your first book project to begin. Manuscript import arrives in Phase 2.
          </p>
          <Button
            onClick={() => setOpen(true)}
            data-testid="empty-create-project-button"
            className="mt-6 gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400"
          >
            <Plus className="h-4 w-4" /> New production
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {filtered.map((p, i) => (
            <motion.div
              key={p.id}
              data-testid="project-card"
              onClick={() => navigate(`/studio/projects/${p.id}`)}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              className="group relative cursor-pointer overflow-hidden rounded-xl border border-border bg-[#12161f] p-5 text-left transition-all duration-200 hover:border-amber-500/40 hover:shadow-[0_0_0_1px_rgba(245,158,11,0.15)]"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-amber-500/10 ring-1 ring-amber-500/25">
                    <Gem className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-slate-100">{p.title}</h3>
                    <p className="text-xs text-slate-500">{p.author || "Unknown author"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    data-testid={`archive-project-${p.id}`}
                    onClick={(e) => archiveProject(p.id, e)}
                    title="Archive project"
                    className="rounded-md p-1.5 text-slate-500 opacity-0 transition-all hover:bg-slate-800/60 hover:text-amber-300 group-hover:opacity-100"
                  >
                    <Archive className="h-4 w-4" />
                  </button>
                  <ArrowRight className="h-4 w-4 text-slate-600 transition-transform group-hover:translate-x-1 group-hover:text-amber-400" />
                </div>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <Badge
                  variant="outline"
                  className={`rounded-full border-0 px-2.5 py-0.5 text-[10px] uppercase tracking-wider ring-1 ${
                    STATUS_STYLES[p.status] || STATUS_STYLES.draft
                  }`}
                >
                  {p.status.replace(/_/g, " ")}
                </Badge>
                <Badge
                  variant="outline"
                  className="rounded-full border-border bg-slate-900/50 px-2.5 py-0.5 font-mono text-[10px] text-slate-400"
                >
                  {MODE_LABELS[p.production_mode]}
                </Badge>
                {p.target_word_count ? (
                  <Badge
                    variant="outline"
                    className="rounded-full border-border bg-slate-900/50 px-2.5 py-0.5 font-mono text-[10px] text-slate-400"
                  >
                    {p.target_word_count.toLocaleString()} words
                  </Badge>
                ) : null}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {archived.length > 0 && (
        <div className="mt-10">
          <button
            data-testid="toggle-archived"
            onClick={() => setShowArchived((s) => !s)}
            className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-slate-500 hover:text-amber-300"
          >
            <Archive className="h-3.5 w-3.5" /> Archived ({archived.length}) {showArchived ? "▾" : "▸"}
          </button>
          {showArchived && (
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="archived-list">
              {archived.map((p) => (
                <div key={p.id} data-testid={`archived-project-${p.id}`} className="flex items-center justify-between rounded-lg border border-border bg-[#0d1016] p-4">
                  <div>
                    <div className="font-serif text-base text-slate-300">{p.title}</div>
                    <div className="text-xs text-slate-500">{p.author || "Unknown author"}</div>
                  </div>
                  <Button
                    data-testid={`restore-project-${p.id}`}
                    onClick={(e) => restoreProject(p.id, e)}
                    variant="outline"
                    className="h-8 gap-1.5 border-emerald-500/40 text-emerald-200 text-xs hover:bg-emerald-500/10"
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> Restore
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <CreateProjectModal open={open} onOpenChange={setOpen} onCreated={load} />
    </div>
  );
}
